import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { Icon } from '../../../components/base/Icon';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { useListLinkNavigation } from '../../hooks/useListLinkNavigation';
import type {
	EnhancedFieldConfiguration,
	ListBuilderCallbacks,
	StyleConfiguration,
} from '../../types';
import { SIZE_MAP } from '../../utils/colorRuleEngine';
import { getFieldDisplayProperties } from './displayHelpers';
import { handleTruncationTooltip } from './truncationTooltip';

interface ChipDisplayProps {
	field: EnhancedFieldConfiguration;
	value: any;
	data: any;
	onRemove?: () => void;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

export function ChipDisplay({
	field,
	value,
	data,
	onRemove,
	listKey,
	objects,
	callbacks,
}: ChipDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { link } = field;
	// styleConfig is optional on FieldConfiguration; fall back to defaults
	// instead of crashing on configs that omit it.
	const styleConfig: Partial<StyleConfiguration> = field.styleConfig ?? {};

	// Use the shared link navigation hook
	const { linkUrl, handleLinkClick, isCallback } = useListLinkNavigation({
		field,
		data,
		listKey,
		objects,
		callbacks,
	});

	// Get display value and color using value mapping
	const { displayValue, colorValue } = getFieldDisplayProperties(
		field,
		value,
		data,
	);

	// Get size configuration - fallback to 'md' if size key is not found in SIZE_MAP
	const sizeConfig = SIZE_MAP[styleConfig.size ?? 'md'] ?? SIZE_MAP.md;

	// Build container classes
	const getSizeClasses = () => {
		switch (styleConfig.size) {
			case 'xs':
				return 'px-2 py-0.5 text-xs min-h-[20px]';
			case 'sm':
				return 'px-2.5 py-1 text-sm min-h-[24px]';
			case 'lg':
				return 'px-4 py-2 text-base min-h-[36px]';
			case 'xl':
				return 'px-5 py-2.5 text-lg min-h-[44px]';
			default: // md
				return 'px-3 py-1.5 text-sm min-h-[32px]';
		}
	};

	const getVariantClasses = () => {
		const baseClasses =
			'inline-flex items-center gap-1.5 font-semibold rounded-full';

		switch (styleConfig.variant) {
			case 'outline':
				return `${baseClasses} bg-transparent border-[1.5px]`;
			case 'ghost':
				return baseClasses;
			default: // filled
				return baseClasses;
		}
	};

	// Resolve theme-aware colors
	const resolvedBg = colorValue?.bg
		? resolveThemeColor(colorValue.bg, isDark)
		: null;
	const resolvedText = colorValue?.text
		? resolveThemeColor(colorValue.text, isDark)
		: null;
	const resolvedBorder = colorValue?.border
		? resolveThemeColor(colorValue.border, isDark)
		: null;

	// Apply color styles with theme-aware resolution
	const getColorStyles = () => {
		const styles: React.CSSProperties = {};

		if (styleConfig.variant === 'outline') {
			styles.borderColor =
				resolvedBorder ?? resolvedBg ?? colors.values.borderDefault;
			styles.color = resolvedText ?? colors.values.textPrimary;
		} else if (styleConfig.variant === 'ghost') {
			// Ghost variant - use very light background
			const bgColor = resolvedBg ?? colors.values.backgroundSecondary;
			styles.backgroundColor = `${bgColor}20`; // 20 = ~12% opacity
			styles.color = resolvedText ?? colors.values.textPrimary;
		} else {
			// Filled variant
			styles.backgroundColor = resolvedBg ?? colors.values.backgroundSecondary;
			styles.color = resolvedText ?? colors.values.textPrimary;
		}

		return styles;
	};

	const iconColor =
		styleConfig.variant === 'outline' || styleConfig.variant === 'ghost'
			? (resolvedBg ?? colors.values.textPrimary)
			: (resolvedText ?? colors.values.textPrimary);

	const handleRemoveClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onRemove?.();
	};

	// Check if this field has linking enabled
	const isLinkEnabled =
		link?.enabled &&
		(link.type === 'callback' ||
			link.type === 'custom' ||
			data?.id != null ||
			data?._id != null);

	const containerClasses = cn(getVariantClasses(), getSizeClasses());
	const containerStyles = getColorStyles();

	const chipContent = (
		<>
			<span className="truncate" onMouseEnter={handleTruncationTooltip}>
				{t(String(displayValue))}
			</span>

			{onRemove && (
				<button
					onClick={handleRemoveClick}
					className="ml-0.5 hover:opacity-70 transition-opacity"
					type="button"
				>
					<Icon
						icon={faTimes}
						size={sizeConfig.iconSize - 2}
						color={iconColor}
					/>
				</button>
			)}
		</>
	);

	if (isLinkEnabled) {
		if (!isCallback && linkUrl != null) {
			return (
				<a
					href={linkUrl}
					onClick={handleLinkClick}
					className={cn(
						containerClasses,
						'hover:opacity-80 transition-opacity cursor-pointer no-underline',
					)}
					style={containerStyles}
				>
					{chipContent}
				</a>
			);
		}

		return (
			<button
				onClick={handleLinkClick}
				className={cn(
					containerClasses,
					'hover:opacity-80 transition-opacity cursor-pointer',
				)}
				style={containerStyles}
				type="button"
			>
				{chipContent}
			</button>
		);
	}

	return (
		<div className={containerClasses} style={containerStyles}>
			{chipContent}
		</div>
	);
}
